import React from "react";
import UIToggle from "../../Common/UIToggle";
import UIIcon from "../../Common/UIIcon";
import InstructorRatingsRecentList from "../../Common/InstructorRatingsRecentList";
import RatingStars from "../../Common/RatingStars";
import { getRatingAccentColor } from "../../Common/ratingPalette";
import {
   monthSlotKey,
   formatMonthTabLabel,
   formatRatingNumber,
   ratingToChartPercent,
   RATING_CHART_GOAL,
   getTrendBarClass,
   getTrendBarStyle,
} from "./instructorProfileShared";

export default function InstructorStatsTab({
   shiftRatingsMonth,
   ratingsLoading,
   visibleMonthWindow,
   ratingsFullYear,
   ratingsAnchor,
   currentMonthSlot,
   selectRatingsMonth,
   canShiftMonthForward,
   ratingsYear,
   setRatingsFullYear,
   ratingsAll,
   ratingsError,
   selectedPeriodCardStyle,
   selectedPeriodLabel,
   selectedPeriodStats,
   minCardStyle,
   maxCardStyle,
   ratingsTrendBuckets,
   statsTrendPeriodKey,
   periodFilteredRatings,
}) {
  return (
               <div className="instructorProfileUI__section instructorProfileUI__section--stats">
            <div className="instructorProfileUI__statsMonthRow">
               <button
                  type="button"
                  className="instructorProfileUI__tab instructorProfileUI__statsMonthNav"
                  onClick={() => shiftRatingsMonth(-1)}
                  disabled={ratingsLoading}
                  aria-label="Luna anterioară"
               >
                  <UIIcon
                     name="arrow"
                     className="instructorProfileUI__statsMonthNavIcon"
                  />
               </button>

               <div className="instructorProfileUI__statsTabShell">
                  <div
                     className="instructorProfileUI__statsMonthTabs"
                     role="tablist"
                     aria-label="Luni"
                  >
                     {visibleMonthWindow.map((slot) => {
                        const isActive =
                           !ratingsFullYear &&
                           slot.year === ratingsAnchor.year &&
                           slot.month === ratingsAnchor.month;
                        const isCurrent =
                           slot.year === currentMonthSlot.year &&
                           slot.month === currentMonthSlot.month;

                        return (
                           <button
                              key={monthSlotKey(slot)}
                              type="button"
                              role="tab"
                              aria-selected={isActive}
                              className={`instructorProfileUI__tab instructorProfileUI__statsMonthTab ${
                                 isActive ? "is-active" : ""
                              } ${isCurrent ? "is-current" : ""}`}
                              onClick={() => selectRatingsMonth(slot)}
                              disabled={ratingsLoading}
                           >
                              {formatMonthTabLabel(slot)}
                           </button>
                        );
                     })}
                  </div>
               </div>

               <button
                  type="button"
                  className="instructorProfileUI__tab instructorProfileUI__statsMonthNav"
                  onClick={() => shiftRatingsMonth(1)}
                  disabled={ratingsLoading || !canShiftMonthForward}
                  aria-label="Luna următoare"
               >
                  <UIIcon
                     name="arrow"
                     className="instructorProfileUI__statsMonthNavIcon instructorProfileUI__statsMonthNavIcon--next"
                  />
               </button>
            </div>

            <div className="instructorProfileUI__statsYearRow">
               <span className="instructorProfileUI__fieldLabel">
                  Tot anul {ratingsYear}
               </span>
               <UIToggle
                  checked={ratingsFullYear}
                  onChange={(nextValue) => setRatingsFullYear(nextValue)}
                  disabled={ratingsLoading}
                  aria-label={`Afișează tot anul ${ratingsYear}`}
               />
            </div>

            {ratingsLoading && !ratingsAll.length && (
               <p className="instructorProfileUI__status">
                  Se încarcă statisticile...
               </p>
            )}

            {!ratingsLoading && ratingsError && (
               <p className="instructorProfileUI__error">{ratingsError}</p>
            )}

            {!ratingsError && (ratingsAll.length || !ratingsLoading) && (
               <>
                  <div className="instructorProfileUI__statsSummary">
                     <div
                        className="instructorProfileUI__statsCard instructorProfileUI__statsCard--average"
                        style={selectedPeriodCardStyle}
                     >
                        <span className="instructorProfileUI__fieldLabel">
                           Medie · {selectedPeriodLabel}
                        </span>
                        <span className="instructorProfileUI__statsValue instructorProfileUI__statsValue--average">
                           {formatRatingNumber(
                              selectedPeriodStats.average,
                           )}
                        </span>
                        <RatingStars
                           value={selectedPeriodStats.average}
                           size="lg"
                           accentColor={
                              getRatingAccentColor(
                                 selectedPeriodStats.average,
                              ) || undefined
                           }
                        />
                     </div>

                     <div className="instructorProfileUI__statsCard">
                        <span className="instructorProfileUI__fieldLabel">
                           Evaluări
                        </span>
                        <span className="instructorProfileUI__statsValue">
                           {selectedPeriodStats.total}
                        </span>
                     </div>

                     <div
                        className="instructorProfileUI__statsCard"
                        style={minCardStyle}
                     >
                        <span className="instructorProfileUI__fieldLabel">
                           Minim
                        </span>
                        <span className="instructorProfileUI__statsValue">
                           {formatRatingNumber(selectedPeriodStats.min)}
                        </span>
                     </div>

                     <div
                        className="instructorProfileUI__statsCard"
                        style={maxCardStyle}
                     >
                        <span className="instructorProfileUI__fieldLabel">
                           Maxim
                        </span>
                        <span className="instructorProfileUI__statsValue">
                           {formatRatingNumber(selectedPeriodStats.max)}
                        </span>
                     </div>
                  </div>

                  <div className="instructorProfileUI__statsTrendPanel">
                     <div className="instructorProfileUI__statsTrendHead">
                        <h3 className="instructorProfileUI__statsTitle">
                           Evoluție
                        </h3>
                     </div>

                     <div
                        className="instructorProfileUI__statsTrendChartWrap"
                        style={{
                           "--rating-goal-pct": ratingToChartPercent(
                              RATING_CHART_GOAL,
                           ),
                        }}
                     >
                        <div className="instructorProfileUI__statsTrendYAxis">
                           <span>5</span>
                           <span>4</span>
                           <span>3</span>
                           <span>2</span>
                           <span>1</span>
                        </div>

                        <div className="instructorProfileUI__statsTrendPlot">
                           <div className="instructorProfileUI__statsTrendBarRow">
                              <div
                                 className="instructorProfileUI__statsTrendGoalLine"
                                 aria-hidden="true"
                              />

                              {ratingsTrendBuckets.map((bucket, index) => (
                                 <div
                                    key={bucket.key}
                                    className="instructorProfileUI__statsTrendBarArea"
                                 >
                                    <div
                                       key={`${bucket.key}-${statsTrendPeriodKey}`}
                                       className={`instructorProfileUI__statsTrendBar ${getTrendBarClass(bucket.average)}`}
                                       style={getTrendBarStyle(
                                          bucket.average,
                                          index,
                                       )}
                                    />
                                 </div>
                              ))}
                           </div>

                           <div className="instructorProfileUI__statsTrendLabelRow">
                              {ratingsTrendBuckets.map((bucket) => (
                                 <span
                                    key={`${bucket.key}-label`}
                                    className={`instructorProfileUI__statsTrendLabel ${
                                       bucket.isSelected
                                          ? "is-current"
                                          : ""
                                    }`}
                                 >
                                    {bucket.label}
                                 </span>
                              ))}
                           </div>
                        </div>
                     </div>
                  </div>

                  <div className="instructorProfileUI__statsBlock">
                     <h3 className="instructorProfileUI__statsTitle">
                        Evaluări
                     </h3>

                     {periodFilteredRatings.length > 0 ? (
                        <InstructorRatingsRecentList
                           ratings={periodFilteredRatings}
                        />
                     ) : (
                        <div className="instructorProfileUI__placeholder">
                           Nu există evaluări pentru perioada selectată.
                        </div>
                     )}
                  </div>
               </>
            )}
         </div>
  );
}
